import React, {
	ClipboardEvent, ElementRef, FC, KeyboardEvent, useEffect, useRef, useState,
} from 'react';
import { XIcon } from '@heroicons/react/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { ColumnCellEditorActions, ICellEditor } from '@aleph/shared-components';

import 'wb-reddit/app/routes/superGrid/columnEditors/urls/urls.scss';
import { CreateEventTrackerType, EventTrackerInput } from 'wb-reddit/graphql/generated';

const URLsCellEditor: FC<ICellEditor<EventTrackerInput[] | null> & {
	eventTrackerType?: CreateEventTrackerType }> = (
	params,
) => {
	const { value, setValue } = params;

	const inputRef = useRef<HTMLInputElement>(null);
	const stagedRef = useRef<ElementRef<'div'>>(null);

	const [stagedValue, setStagedValue] = useState('');
	const [search, setSearch] = useState('');

	useEffect(() => {
		if (!stagedValue) {
			inputRef?.current?.focus?.();
		} else {
			stagedRef?.current?.focus?.();
		}
	}, [inputRef, stagedValue]);

	const addStagedValue = () => {
		if (!stagedValue) {
			return;
		}

		setValue([...(value ?? []), {
			url:  stagedValue,
			type: params.eventTrackerType ?? CreateEventTrackerType.View,
		}]);
		setStagedValue('');
		setSearch('');
	};

	const confirmStagedValue = () => {
		if (search) {
			setStagedValue(search);
		}
		addStagedValue();
	};

	const onStagedKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Backspace') {
			if (stagedValue) {
				setStagedValue('');
			}
		}

		if (e.key === 'Enter') {
			if (!stagedValue && !search) {
				params.stopEditing();
			}

			addStagedValue();
		}

		if (e.key === 'Escape') {
			setTimeout(() => {
				params.api.stopEditing(true);
				setTimeout(() => params.api.setFocusedCell(params.rowIndex, params.column));
			});
		}
	};

	const onPaste = (e: ClipboardEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const pastedTexts = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		if (pastedTexts.length === 1) {
			setSearch(pastedTexts[0]);
		}

		if (pastedTexts.length > 0) {
			setValue([
				...(value ?? []),
				...pastedTexts.map(text => ({
					url:  text,
					type: params.eventTrackerType ?? CreateEventTrackerType.View,
				})),
			]);
		}
	};

	return (
		<div
			ref={stagedRef}
			onKeyDown={onStagedKeyDown}
			tabIndex={0}
			className="flex items-center urls-cell-editor gap-[8px] bg-white h-full w-full outline-none"
		>
			<div className="flex h-full flex-1 items-center px-[8px]">
				<AnimatePresence
					mode="wait"
				>
					{
						stagedValue
						&& <motion.div
							initial={{ opacity: 1, x: 0 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ x: 50, opacity: 0 }}
							transition={{ duration: 0.1 }}
							key="stagedTag"
							className="leading-none outline-none justify-between flex-1 flex items-center"
						>
							<div className="bg-gray-700 w-full flex justify-between p-[4px] text-white
								rounded text-[12px]"
							>
								<div>
									{stagedValue}
								</div>

								<div
									className="cursor-pointer"
									onClick={() => setStagedValue('')}
								>
									<XIcon
										height={12}
										width={12}
									/>
								</div>
							</div>
						</motion.div>
					}

					{
						!stagedValue
						&& <div
							className="w-full"
							key="searchTag"
						>
							<input
								ref={inputRef}
								onChange={(e) => setSearch(e.target.value)}
								onPaste={onPaste}
								placeholder='Enter one or more URLs'
								className="w-full"
								autoFocus
								onKeyDown={(e: KeyboardEvent) => {
									if (e.key === 'Enter' && search) {
										setStagedValue(search);
										inputRef?.current?.focus?.();
									}
								}}
							/>
						</div>
					}
				</AnimatePresence>
			</div>

			<ColumnCellEditorActions
				onCancel={
					stagedValue
						? () => setStagedValue('')
						: () => params.api.stopEditing(true)
				}
				onConfirm={
					search
						? () => confirmStagedValue()
						: () => params.api.stopEditing()
				}
				confirmText={stagedValue ? 'ENTER' : 'CONFIRM'}
			/>
		</div>
	);
};

export default URLsCellEditor;
